import React, { useMemo, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { renderInvoiceHtml } from '../utils/invoiceUtils';
import FullScreenPanel from './UI/FullScreenPanel';

const InvoiceModal = ({ visible, loan, onClose }) => {
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType]       = useState('info');

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const invoiceHtml = useMemo(() => (loan ? renderInvoiceHtml(loan, { darkMode: isDark }) : ''), [loan, isDark]);

  if (!visible || !loan) return null;

  const clientLabel = loan.clientName || loan.client_name
    || [loan.first_name, loan.last_name].filter(Boolean).join(' ')
    || '';

  /* ── Print ─────────────────────────────────────────── */
  const openPrintableInvoice = () => {
    try {
      const w = window.open('', '_blank');
      if (!w) { alert('Popup blocked. Please allow popups for this site.'); return; }
      w.document.open();
      w.document.write(invoiceHtml);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 600);
    } catch (err) {
      console.error('Print error:', err);
      alert('Failed to open printable invoice.');
    }
  };

  /* ── HTML download ──────────────────────────────────── */
  const downloadInvoiceHtml = () => {
    try {
      const blob = new Blob([invoiceHtml], { type: 'text/html' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `invoice_loan_${loan.id}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('HTML download error:', err);
      alert('Failed to download invoice.');
    }
  };

  /* ── PDF download ───────────────────────────────────── */
  const downloadInvoicePdf = () => {
    let wrapper = null;
    try {
      setStatusMessage('Generating PDF…');
      setStatusType('info');

      // Parse the invoice HTML and clone content into the main document so
      // html2canvas captures it without cross-document iframe restrictions.
      const parsed = new DOMParser().parseFromString(invoiceHtml, 'text/html');
      const pageEl  = parsed.querySelector('.page');
      const styleEl = parsed.querySelector('style');
      if (!pageEl) {
        setStatusMessage('PDF generation failed – page element not found.');
        setStatusType('error');
        return;
      }

      wrapper = document.createElement('div');
      wrapper.style.cssText =
        'position:fixed;left:-9999px;top:0;width:820px;pointer-events:none;background:#e2e8f0';
      if (styleEl) {
        const s = document.createElement('style');
        s.textContent = styleEl.textContent;
        wrapper.appendChild(s);
      }
      wrapper.appendChild(pageEl.cloneNode(true));
      document.body.appendChild(wrapper);

      const target = wrapper.querySelector('.page');

      // Allow a paint cycle before capture
      setTimeout(() => {
        const opts = {
          margin:      [0, 0, 0, 0],
          filename:    `invoice_loan_${loan.id}.pdf`,
          image:       { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#e2e8f0',
            windowWidth: 820,
          },
          jsPDF:     { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        };
        html2pdf().from(target).set(opts).save()
          .then(() => {
            if (wrapper && wrapper.parentNode) document.body.removeChild(wrapper);
            setStatusMessage('PDF generated successfully!');
            setStatusType('success');
            setTimeout(() => setStatusMessage(''), 3000);
          })
          .catch(err => {
            if (wrapper && wrapper.parentNode) document.body.removeChild(wrapper);
            console.error('PDF error:', err);
            setStatusMessage('Failed to generate PDF.');
            setStatusType('error');
          });
      }, 300);
    } catch (err) {
      if (wrapper && wrapper.parentNode) document.body.removeChild(wrapper);
      console.error('PDF setup error:', err);
      setStatusMessage('Failed to generate PDF.');
      setStatusType('error');
    }
  };

  /* ── Render ─────────────────────────────────────────── */
  const actionBtns = (
    <div className="flex items-center gap-2 flex-shrink-0">
      <button onClick={openPrintableInvoice} className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 rounded-lg border border-gray-200 dark:border-slate-600 transition-colors">Print</button>
      <button onClick={downloadInvoicePdf} className="px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors">PDF</button>
      <button onClick={downloadInvoiceHtml} className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">HTML</button>
    </div>
  );

  return (
    <FullScreenPanel
      isOpen={visible && !!loan}
      onClose={onClose}
      title={`Invoice — INV-${loan.id}`}
      subtitle={clientLabel || undefined}
      headerRight={actionBtns}
    >
      {statusMessage && (
        <div className={`mb-3 text-sm px-3 py-2 rounded-lg inline-block font-medium ${
          statusType === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
          : statusType === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
        }`}>
          {statusMessage}
        </div>
      )}
      <iframe
        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg"
        style={{ minHeight: '72vh', background: isDark ? '#0f172a' : '#e2e8f0' }}
        srcDoc={invoiceHtml}
        title={`Invoice INV-${loan.id}`}
        sandbox="allow-same-origin allow-popups"
      />
    </FullScreenPanel>
  );
};

export default InvoiceModal;
